// API 响应类型
export interface UnsplashPhoto {
  id: string;
  width: number;
  height: number;
  urls: {
    raw: string;
    full: string;
    regular: string;
    small: string;
    thumb: string;
  };
  user: {
    name: string;
    username: string;
    links: {
      html: string;
    };
  };
  links: {
    html: string;
    download_location: string;
  };
  description?: string;
  alt_description?: string;
}

export interface UnsplashSearchResponse {
  total: number;
  total_pages: number;
  results: UnsplashPhoto[];
}

export interface HitokotoQuote {
  id: number;
  uuid: string;
  hitokoto: string;
  type: string;
  from: string;
  from_who?: string;
  creator: string;
  creator_uid: number;
  reviewer: number;
  commit_from: string;
  created_at: string;
  length: number;
}

// 应用内部类型
export interface ProcessedImage {
  url: string;
  photographer: string;
  photographer_url: string;
  photo_url: string;
  download_location?: string;
  photo_id?: string;
}

export interface ProcessedQuote {
  text: string;
  source: string;
  uuid?: string;
}

export interface DailyContent {
  date: string;
  quote: ProcessedQuote;
  image: ProcessedImage;
}

export interface DateInfo {
  monthDay: string;
  weekDay: string;
  lunar: string;
  dateNumber: string;
}

// 配置类型
export interface ApiConfig {
  unsplash: {
    apiKey: string;
    baseUrl: string;
    defaultQueries: string[];
    orientation: 'landscape' | 'portrait' | 'squarish';
    useHotlinking: boolean;
  };
  pexels: {
    apiKey: string;
    baseUrl: string;
    defaultQueries: string[];
    orientation: string;
    size: string;
  };
  hitokoto: {
    baseUrl: string;
    fallbackUrls?: string[];
    types: string[];
  };
  cache: {
    duration: number;
    key: string;
  };
  imageProvider: 'unsplash' | 'pexels';
}

// Hook 类型
export interface UseImageApiResult {
  getImage: () => Promise<ProcessedImage>;
  isLoading: boolean;
  error: string | null;
}

export interface UseQuoteApiResult {
  getQuote: () => Promise<ProcessedQuote>;
  isLoading: boolean;
  error: string | null;
}

export interface UseCacheResult<T> {
  data: T | null;
  setData: (data: T) => void;
  clearCache: () => void;
  isExpired: boolean;
}

// 组件 Props 类型
export interface QuoteCardProps {
  quote: ProcessedQuote;
  isLoading?: boolean;
}

export interface DateDisplayProps {
  dateInfo: DateInfo;
}

export interface ControlsProps {
  onRefresh: () => void;
  onShare: () => void;
  isLoading?: boolean;
}

export interface MusicPlayerProps {
  playlistUrl?: string;
}

export interface MusicConfig {
  spotify: {
    url: string;
    fallbackUrl: string;
  };
  youtube: {
    url: string;
  };
  provider: 'spotify' | 'youtube';
}

export interface LoadingScreenProps {
  isVisible: boolean;
}

export interface AttributionProps {
  image: ProcessedImage;
  imageProvider: 'unsplash' | 'pexels';
}

// 错误类型
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public provider?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// 事件类型
export type RefreshEvent = 'manual' | 'keyboard' | 'auto';
export type ShareEvent = 'keyboard' | 'button';

// 应用状态类型
export interface AppState {
  content: DailyContent | null;
  isLoading: boolean;
  error: string | null;
  dateInfo: DateInfo;
  backgroundUrl: string;
}

// 环境音响类型
export interface AmbientSound {
  id: string;
  name: string;
  icon: string;
  url: string;
  category: 'nature' | 'noise' | 'urban';
}

export interface AmbientPlayerState {
  isPlaying: boolean;
  currentSound: AmbientSound | null;
  volume: number;
  isExpanded: boolean;
}

export interface UseAmbientPlayerResult {
  state: AmbientPlayerState;
  actions: {
    play: (sound: AmbientSound) => void;
    stop: () => void;
    setVolume: (volume: number) => void;
    toggleExpanded: () => void;
  };
}

export interface AmbientPlayerProps {
  className?: string;
}

// 每日专注事项便签类型
export interface DailyTask {
  id: string;
  content: string;
  completed: boolean;
  createdAt: Date;
}

export interface StickyNoteState {
  tasks: DailyTask[];
  position: { x: number; y: number };
  isVisible: boolean;
  isMinimized: boolean;
  isEditing: string | null; // 正在编辑的任务ID
}

export interface UseStickyNoteResult {
  state: StickyNoteState;
  actions: {
    addTask: (content: string) => void;
    updateTask: (id: string, content: string) => void;
    toggleTask: (id: string) => void;
    deleteTask: (id: string) => void;
    updatePosition: (position: { x: number; y: number }) => void;
    toggleVisibility: () => void;
    toggleMinimize: () => void;
    startEditing: (id: string) => void;
    stopEditing: () => void;
    resetTasks: () => void;
  };
}

export interface StickyNoteProps {
  className?: string;
}

export interface TaskItemProps {
  task: DailyTask;
  isEditing: boolean;
  onToggle: (id: string) => void;
  onUpdate: (id: string, content: string) => void;
  onDelete: (id: string) => void;
  onStartEdit: (id: string) => void;
  onStopEdit: () => void;
}